import { useCallback, useMemo, useState } from 'react'
import { ApiError } from '../../api/problem'
import type { Notice } from '../../lib/envelope'
import type { components } from '../../api/generated/schema'
import { characterApi, optimisticFate, optimisticStress, useSectionMutation } from './mutations'

type Schema = components['schemas']

/**
 * Every editable section's mutation, in one place.
 *
 * Bundling them is what lets notices and failures be rendered once rather than
 * per card: each mutation reports through the same two callbacks, so the sheet
 * has a single banner area instead of thirteen.
 */
export function useCharacterEditing(characterId: number, shapeId: number | null, canEdit: boolean) {
  const [notices, setNotices] = useState<Notice[]>([])
  const [failure, setFailure] = useState<string | null>(null)

  const onEnvelope = useCallback((envelope: { notices: Notice[] }) => {
    // Server-authored messages about what the change actually did — including
    // things the caller never asked for, like a stress box kept alive.
    setNotices(envelope.notices)
    setFailure(null)
  }, [])

  const onFailure = useCallback((error: unknown) => {
    setNotices([])
    setFailure(describeFailure(error))
  }, [])

  const base = useMemo(
    () => ({ characterId, shapeId, onEnvelope, onFailure }),
    [characterId, shapeId, onEnvelope, onFailure],
  )

  const stress = useSectionMutation<{ slotId: number; isStressed: boolean }>({
    ...base,
    request: ({ slotId, isStressed }) =>
      characterApi.setStressMark(characterId, slotId, isStressed),
    optimistic: optimisticStress,
  })

  const addStressBox = useSectionMutation<{ type: string }>({
    ...base,
    request: ({ type }) => characterApi.addStressBox(characterId, shapeId, type),
  })

  const clearStress = useSectionMutation<void>({
    ...base,
    request: () => characterApi.clearStress(characterId, shapeId),
  })

  const feed = useSectionMutation<void>({
    ...base,
    request: () => characterApi.feed(characterId, shapeId),
  })

  const fate = useSectionMutation<{ fatePoints: number }>({
    ...base,
    request: ({ fatePoints }) => characterApi.setFatePoints(characterId, fatePoints),
    optimistic: optimisticFate,
  })

  const totalRefresh = useSectionMutation<{ totalRefresh: number }>({
    ...base,
    request: ({ totalRefresh: value }) => characterApi.setTotalRefresh(characterId, value),
  })

  const skillLevel = useSectionMutation<{ skillId: number; level: number }>({
    ...base,
    request: ({ skillId, level }) =>
      characterApi.setSkillLevel(characterId, shapeId, skillId, level),
  })

  const removeSkill = useSectionMutation<{ skillId: number }>({
    ...base,
    request: ({ skillId }) => characterApi.removeSkill(characterId, shapeId, skillId),
  })

  const skillPool = useSectionMutation<{ poolOption: string }>({
    ...base,
    request: ({ poolOption }) => characterApi.setSkillPool(characterId, shapeId, poolOption),
  })

  /**
   * Nudges the point budget off a named power level. The pool is stored as the total itself,
   * so a total between two named levels is a legal state, not a broken one.
   */
  const adjustSkillPool = useSectionMutation<{ delta: number }>({
    ...base,
    request: ({ delta }) => characterApi.adjustSkillPool(characterId, shapeId, delta),
  })

  /**
   * The pyramid's apex. Character-wide rather than per-shape, so a shapeshifter's forms share
   * it — the `shapeId` here only scopes the sections the envelope brings back.
   */
  const maxSkillLevel = useSectionMutation<{ maxLevel: number }>({
    ...base,
    request: ({ maxLevel }) => characterApi.setMaxSkillLevel(characterId, shapeId, maxLevel),
  })

  const aspects = useSectionMutation<Schema['UpdateAspectsRequest']>({
    ...base,
    request: (body) => characterApi.updateAspects(characterId, body),
  })

  const bio = useSectionMutation<Schema['UpdateBioRequest']>({
    ...base,
    request: (body) => characterApi.updateBio(characterId, body),
  })

  const setConsequence = useSectionMutation<{
    slotId: number
    name: string
    effect: string | null
  }>({
    ...base,
    request: ({ slotId, name, effect }) =>
      characterApi.setConsequence(characterId, slotId, name, effect),
  })

  const clearConsequence = useSectionMutation<{ slotId: number }>({
    ...base,
    request: ({ slotId }) => characterApi.clearConsequence(characterId, slotId),
  })

  /**
   * Adding from the seeded master list. Present for completeness, but not
   * reachable from the UI: the API exposes no endpoint that returns those
   * templates, so a client cannot learn an `abilityId` to pass. Use
   * {@link addAbilityCustom} until it does.
   */
  const addAbility = useSectionMutation<{ abilityId: number }>({
    ...base,
    request: ({ abilityId }) =>
      characterApi.addAbilityFromTemplate(characterId, shapeId, abilityId),
  })

  const addAbilityCustom = useSectionMutation<Schema['AddCustomAbilityRequest']>({
    ...base,
    request: (body) => characterApi.addCustomAbility(characterId, shapeId, body),
  })

  const removeAbility = useSectionMutation<{ abilityId: number }>({
    ...base,
    request: ({ abilityId }) => characterApi.removeAbility(characterId, shapeId, abilityId),
  })

  /** Which shapes an ability applies to. An empty list means "all of them". */
  const setAbilityShapes = useSectionMutation<{ abilityId: number; shapeIds: number[] }>({
    ...base,
    request: ({ abilityId, shapeIds }) =>
      characterApi.setAbilityShapes(characterId, shapeId, abilityId, shapeIds),
  })

  const addAbilityExtraStress = useSectionMutation<{
    abilityId: number
    type: string
    value: number
  }>({
    ...base,
    request: ({ abilityId, type, value }) =>
      characterApi.addAbilityExtraStress(characterId, shapeId, abilityId, type, value),
  })

  const setAbilityVampirismRelated = useSectionMutation<{
    abilityId: number
    isVampirismRelated: boolean
  }>({
    ...base,
    request: ({ abilityId, isVampirismRelated }) =>
      characterApi.setAbilityVampirismRelated(characterId, shapeId, abilityId, isVampirismRelated),
  })

  const removeAbilityExtraStress = useSectionMutation<{ extraStressId: number }>({
    ...base,
    request: ({ extraStressId }) =>
      characterApi.removeAbilityExtraStress(characterId, shapeId, extraStressId),
  })

  const addAbilityExtraConsequence = useSectionMutation<{
    abilityId: number
    type: string
    level: string
  }>({
    ...base,
    request: ({ abilityId, type, level }) =>
      characterApi.addAbilityExtraConsequence(characterId, shapeId, abilityId, type, level),
  })

  const removeAbilityExtraConsequence = useSectionMutation<{ extraConsequenceId: number }>({
    ...base,
    request: ({ extraConsequenceId }) =>
      characterApi.removeAbilityExtraConsequence(characterId, shapeId, extraConsequenceId),
  })

  const addEquipment = useSectionMutation<Schema['EquipmentRequest']>({
    ...base,
    request: (body) => characterApi.addEquipment(characterId, body),
  })

  const setEquipped = useSectionMutation<{ itemId: number; isEquipped: boolean }>({
    ...base,
    request: ({ itemId, isEquipped }) => characterApi.setEquipped(characterId, itemId, isEquipped),
  })

  const removeEquipment = useSectionMutation<{ itemId: number }>({
    ...base,
    request: ({ itemId }) => characterApi.removeEquipment(characterId, itemId),
  })

  const addFocusItem = useSectionMutation<Schema['FocusItemRequest']>({
    ...base,
    request: (body) => characterApi.addFocusItem(characterId, body),
  })

  const removeFocusItem = useSectionMutation<{ itemId: number }>({
    ...base,
    request: ({ itemId }) => characterApi.removeFocusItem(characterId, itemId),
  })

  const addEnchantedItem = useSectionMutation<Schema['EnchantedItemRequest']>({
    ...base,
    request: (body) => characterApi.addEnchantedItem(characterId, body),
  })

  const useEnchantedItem = useSectionMutation<{ itemId: number }>({
    ...base,
    request: ({ itemId }) => characterApi.useEnchantedItem(characterId, itemId),
  })

  const refreshEnchantedItem = useSectionMutation<{ itemId: number }>({
    ...base,
    request: ({ itemId }) => characterApi.refreshEnchantedItem(characterId, itemId),
  })

  const refreshAllEnchantedItems = useSectionMutation<void>({
    ...base,
    request: () => characterApi.refreshAllEnchantedItems(characterId),
  })

  const removeEnchantedItem = useSectionMutation<{ itemId: number }>({
    ...base,
    request: ({ itemId }) => characterApi.removeEnchantedItem(characterId, itemId),
  })

  const addRoteSpell = useSectionMutation<Schema['RoteSpellRequest']>({
    ...base,
    request: (body) => characterApi.addRoteSpell(characterId, body),
  })

  const removeRoteSpell = useSectionMutation<{ spellId: number }>({
    ...base,
    request: ({ spellId }) => characterApi.removeRoteSpell(characterId, spellId),
  })

  const addSpecialization = useSectionMutation<Schema['SpecializationRequest']>({
    ...base,
    request: (body) => characterApi.addSpecialization(characterId, body),
  })

  const removeSpecialization = useSectionMutation<{ specializationId: number }>({
    ...base,
    request: ({ specializationId }) =>
      characterApi.removeSpecialization(characterId, specializationId),
  })

  const all = [
    stress,
    addStressBox,
    clearStress,
    feed,
    fate,
    totalRefresh,
    skillLevel,
    removeSkill,
    skillPool,
    adjustSkillPool,
    maxSkillLevel,
    aspects,
    bio,
    setConsequence,
    clearConsequence,
    addAbility,
    addAbilityCustom,
    removeAbility,
    setAbilityShapes,
    addAbilityExtraStress,
    removeAbilityExtraStress,
    setAbilityVampirismRelated,
    addAbilityExtraConsequence,
    removeAbilityExtraConsequence,
    addEquipment,
    setEquipped,
    removeEquipment,
    addFocusItem,
    removeFocusItem,
    addEnchantedItem,
    useEnchantedItem,
    refreshEnchantedItem,
    refreshAllEnchantedItems,
    removeEnchantedItem,
    addRoteSpell,
    removeRoteSpell,
    addSpecialization,
    removeSpecialization,
  ]

  return {
    canEdit,
    notices,
    failure,
    dismiss: useCallback(() => {
      setNotices([])
      setFailure(null)
    }, []),
    /**
     * True while any mutation is in flight. Non-optimistic edits show a pending
     * state rather than a guessed one, because their cascades land on
     * server-computed budgets.
     */
    isPending: all.some((mutation) => mutation.isPending),
    stress,
    addStressBox,
    clearStress,
    feed,
    fate,
    totalRefresh,
    skillLevel,
    removeSkill,
    skillPool,
    adjustSkillPool,
    maxSkillLevel,
    aspects,
    bio,
    setConsequence,
    clearConsequence,
    addAbility,
    addAbilityCustom,
    removeAbility,
    setAbilityShapes,
    addAbilityExtraStress,
    removeAbilityExtraStress,
    setAbilityVampirismRelated,
    addAbilityExtraConsequence,
    removeAbilityExtraConsequence,
    addEquipment,
    setEquipped,
    removeEquipment,
    addFocusItem,
    removeFocusItem,
    addEnchantedItem,
    useEnchantedItem,
    refreshEnchantedItem,
    refreshAllEnchantedItems,
    removeEnchantedItem,
    addRoteSpell,
    removeRoteSpell,
    addSpecialization,
    removeSpecialization,
  }
}

export type CharacterEditing = ReturnType<typeof useCharacterEditing>

/**
 * Turns a failed mutation into something worth reading.
 *
 * A 409 is the interesting case: the API uses it for a rule the domain forbids,
 * and its `detail` names the rule. Swallowing that into "something went wrong"
 * would throw away the only part the user can act on.
 */
function describeFailure(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something went wrong.'

  switch (error.kind) {
    case 'conflict':
      return error.detail ?? error.title ?? 'That change is not allowed.'
    case 'forbidden':
      return 'Only the owner can change this character.'
    case 'not-found':
      return 'That is no longer there — reload the sheet.'
    case 'validation':
      return (
        error
          .fieldMessages()
          .map((entry) => entry.message)
          .join(' ') || error.message
      )
    case 'network':
      return 'Could not reach the server. Your change was not saved.'
    default:
      return error.message
  }
}
